import { useParams } from "react-router-dom";
import {
  useBundle,
  divisionBracket,
  teamName,
  slotLabel,
  bundleViewState,
} from "../../lib/bundle";
import { bracketRounds } from "../../lib/bracket";
import { gameDayTime, gameLocation } from "../../lib/format";
import { isGameLive, useNow } from "../../lib/liveStatus";
import type { Bundle } from "../../lib/types";
import type { ResolvedBracketGame, ResolvedSlot } from "@alpha/engine";
import { DivisionPageHeader } from "../../components/DivisionTabs";
import {
  ArrowUpRightIcon,
  Badge,
  Card,
  EmptyState,
  GameStatusBadge,
  LoadingBlock,
  MapPinIcon,
  stagger,
  TrophyIcon,
  cx,
} from "../../components/ui";

/** Public single-elimination bracket (spec §10): scrollable columns per round. */
export function Bracket() {
  const { tid, did } = useParams();
  const q = useBundle(tid);
  // Above the early returns below: a hook beside the derived values would be
  // conditional. One interval for the page, not one per card.
  const now = useNow();
  const bundle = q.data;

  const view = bundleViewState(q);
  if (view === "notFound")
    return <EmptyState title="Tournament not found" className="mx-auto max-w-md" />;
  if (view === "error")
    return (
      <EmptyState
        title="Couldn't load this tournament"
        hint="Check your connection and try again."
        className="mx-auto max-w-md"
      />
    );
  if (view === "loading" || !bundle) return <LoadingBlock />;
  const division = bundle.divisions.find((d) => d.id === did);
  if (!division) return <EmptyState title="Division not found" className="mx-auto max-w-md" />;

  const resolved = divisionBracket(bundle, division.id);
  const locked = Boolean(division.lockedSeeds);

  const rounds = bracketRounds(resolved.games);

  return (
    <div className="space-y-8">
      {/*
        The chrome above the bracket used to be centred in a 3xl column while
        the rounds ran the full width beneath it — two different page edges on
        one page. It all shares the bracket's left edge now, which is also the
        edge the schedule and standings use.
      */}
      <DivisionPageHeader bundle={bundle} division={division} />

      {resolved.games.length === 0 ? (
        <div className="max-w-2xl">
          <EmptyState
            icon={<TrophyIcon className="h-5 w-5" />}
            title="The bracket has not been created yet"
            hint="It appears here once pool play wraps up and seeds are set."
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {!locked ? (
              <Badge tone="amber" pulse>
                Provisional — seeds not final
              </Badge>
            ) : null}
          </div>

          {resolved.championId ? (
            // Held to roughly two bracket columns: a champion is a moment, not
            // a banner that needs the whole 1536px to say one team's name.
            <div className="w-full max-w-md">
              <Card className="animate-scale-in overflow-hidden">
                <div className="flex items-center gap-4 bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-4 text-brandFg">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/25">
                    <TrophyIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-100">
                      Champion
                    </p>
                    <p className="font-display text-lg font-bold">
                      {teamName(bundle, resolved.championId)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          ) : null}

          <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
            <div className="flex gap-4 sm:gap-8">
              {rounds.map((round, ri) => (
                <div
                  key={round.round}
                  className="flex w-56 shrink-0 animate-rise flex-col gap-3 sm:w-64"
                  style={stagger(ri + 1)}
                >
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {round.name}
                  </h2>
                  <div className="flex flex-1 flex-col justify-around gap-4">
                    {round.games.map((g) => (
                      <MatchCard key={g.gameId} bundle={bundle} g={g} now={now} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MatchCard({ bundle, g, now }: { bundle: Bundle; g: ResolvedBracketGame; now: number }) {
  const raw = bundle.games.find((x) => x.id === g.gameId);
  const showScore = raw?.status === "final" || raw?.status === "forfeit";

  // When and where the director put this game. Day AND time, because bracket
  // games on one card can be a day apart, and this card has no date heading
  // above it the way the schedule's rows do. Absent until it is scheduled —
  // a bracket is usually built before its times are assigned, and an empty
  // footer on every card would be worse than none.
  const when = gameDayTime(raw?.scheduledAt, bundle.tournament.timezone);
  const where = raw ? gameLocation(bundle.venues, raw) : null;

  return (
    <Card className="overflow-hidden text-sm transition-shadow duration-200 hover:shadow-card-hover">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        <span>Game {g.gameNumber}</span>
        {/*
          Mutually exclusive, unlike the guards inside `GameStatusBadge`: live
          needs a `scheduled` game and Final needs a `final` one, so the order
          here is presentational and safe to reorder. That also means this arm
          is only ever reached for a scheduled game — which is why `status` is
          passed through rather than hardcoded, and why leaving `hideScheduled`
          off costs nothing: the badge reads `live` before it looks at it.
        */}
        {raw && isGameLive(raw, bundle.tournament.config, now) ? (
          <GameStatusBadge status={raw.status} live />
        ) : raw?.status === "final" ? (
          // Left as this strip's own bare text rather than routed through
          // GameStatusBadge like the schedule does. A bracket shows many
          // finished games at once and rarely more than one live one, so giving
          // every Final the same pill weight as Live would drown the one card
          // worth looking at. The asymmetry is the point, not an oversight.
          <span className="font-semibold text-navy-900">Final</span>
        ) : null}
      </div>
      <SlotRow
        bundle={bundle}
        slot={g.home}
        winner={g.winnerId}
        score={showScore ? raw?.homeScore : undefined}
      />
      <div className="border-t border-slate-100" />
      <SlotRow
        bundle={bundle}
        slot={g.away}
        winner={g.winnerId}
        score={showScore ? raw?.awayScore : undefined}
      />
      {when || where ? (
        <div className="space-y-0.5 border-t border-slate-100 bg-slate-50/40 px-3.5 py-2 text-xs text-slate-400">
          {when ? <p>{when}</p> : null}
          {where ? (
            <p>
              {where.href ? (
                <a
                  href={where.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 transition-colors hover:text-brandText"
                >
                  <MapPinIcon className="h-3.5 w-3.5" />
                  {where.text}
                  <ArrowUpRightIcon className="h-3 w-3" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <MapPinIcon className="h-3.5 w-3.5" />
                  {where.text}
                </span>
              )}
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function SlotRow({
  bundle,
  slot,
  winner,
  score,
}: {
  bundle: Bundle;
  slot: ResolvedSlot;
  winner: string | null;
  score?: number;
}) {
  const isWinner = slot.teamId !== null && slot.teamId === winner;
  const tbd = slot.teamId === null;
  return (
    <div
      className={cx(
        "flex items-center justify-between gap-2 px-3.5 py-2.5 transition-colors",
        isWinner && "bg-brand-50/60",
      )}
    >
      <span
        className={cx(
          "truncate",
          tbd && "italic text-slate-400",
          isWinner ? "font-semibold text-navy-950" : !tbd && "text-slate-600",
        )}
      >
        {slotLabel(bundle, slot)}
      </span>
      {score !== undefined ? (
        <span
          className={cx(
            "font-semibold tabular-nums",
            isWinner ? "text-brandText" : "text-slate-400",
          )}
        >
          {score}
        </span>
      ) : null}
    </div>
  );
}
